import React, { useState, useEffect, useCallback } from 'react';
import { X, UserPlus, Clock, Check, XCircle, Building2, RefreshCw } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api } from '../../utils/api';

const FleetInvitesModal = ({ onClose }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/group-view/fleet-invites/incoming');
      setInvites(data.invites || []);
    } catch {
      // Table may not exist
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const respond = async (inviteId, status) => {
    try {
      await api(`/group-view/fleet-invites/${inviteId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      toast.success(status === 'ACCEPTED' ? 'Invite accepted' : 'Invite declined');
      loadInvites();
    } catch (err) {
      toast.error(err.message || 'Failed to respond');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const pending = invites.filter(i => i.status === 'PENDING');
  const history = invites.filter(i => i.status !== 'PENDING');

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center" data-testid="fleet-invites-modal">
      <div className="bg-slate-800 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-400" />
            <h2 className="text-white font-semibold text-base">Fleet Invites</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadInvites} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition" data-testid="close-fleet-invites">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : invites.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No fleet invites</p>
              <p className="text-xs text-slate-500 mt-1">Invites from businesses will appear here</p>
            </div>
          ) : (
            <>
              {/* Pending Invites */}
              {pending.length > 0 && (
                <div>
                  <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">Pending Invites</h3>
                  <div className="space-y-2">
                    {pending.map(inv => (
                      <div key={inv.invite_id} className="bg-slate-700/40 rounded-xl p-3 border border-emerald-500/20" data-testid={`invite-${inv.invite_id}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 bg-emerald-500/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Building2 className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm">{inv.inviter_name || 'A business'}</p>
                            <p className="text-xs text-slate-400 mt-0.5">wants you to join their fleet</p>
                            {inv.message && (
                              <p className="text-xs text-slate-300 mt-1 italic">"{inv.message}"</p>
                            )}
                            <p className="text-xs text-slate-500 mt-1">{formatDate(inv.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => respond(inv.invite_id, 'ACCEPTED')}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-xs font-medium transition"
                            data-testid={`accept-invite-${inv.invite_id}`}
                          >
                            <Check className="w-3.5 h-3.5" />
                            Accept
                          </button>
                          <button
                            onClick={() => respond(inv.invite_id, 'DECLINED')}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-lg text-xs font-medium transition"
                            data-testid={`decline-invite-${inv.invite_id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* History */}
              {history.length > 0 && (
                <div>
                  <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">History</h3>
                  <div className="space-y-1">
                    {history.map(inv => (
                      <div key={inv.invite_id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-700/20">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-300 flex-1">{inv.inviter_name || 'A business'}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          inv.status === 'ACCEPTED' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                        }`}>{inv.status}</span>
                        <span className="text-xs text-slate-500">{formatDate(inv.responded_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetInvitesModal;
